import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TaskStatusChip, TaskPriorityChip } from "../TaskChips";

import TaskDetailsTab from "./TaskDetailsTab";
import TaskHistoryTab from "./TaskHistoryTab";
import TaskCommentsTab from "./TaskCommentsTab";
import TaskActionsBar from "./TaskActionsBar";

const useStyles = makeStyles(theme => ({
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  titleText: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
    flex: 1
  },
  chipsRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap"
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "40vh"
  },
  actions: {
    flexWrap: "wrap"
  }
}));

// Orquestrador do modal de detalhes — busca a tarefa, controla as abas e
// centraliza as chamadas de ação (edição/iniciar/concluir/transferir/
// cancelar). Cada aba (Detalhes/Histórico/Comentários) controla seu
// próprio estado de paginação/loading; este componente só decide QUANDO
// cada uma deve recarregar (troca de tarefa, ação bem-sucedida).
//
// `readOnly` (opcional, default false — retrocompatível: a página /tasks
// não passa essa prop e continua com todas as ações de sempre) oculta a
// barra de ações inteira (editar/iniciar/concluir/transferir/cancelar) e
// o formulário de novo comentário — usado quando o modal é aberto a
// partir da aba "Tarefas" do contato, que é só de consulta.
const TaskDetailsModal = ({
  taskId,
  open,
  onClose,
  onTaskUpdated,
  onTaskUnavailable,
  readOnly = false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const darkTabStyle = theme.palette.type === "dark"
    ? { color: theme.palette.common.white }
    : undefined;
  const darkTabIndicatorProps = theme.palette.type === "dark"
    ? { style: { backgroundColor: theme.palette.common.white } }
    : undefined;
  const { user } = useContext(AuthContext);

  const [task, setTask] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [isEditing, setIsEditing] = useState(false);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);

  const isMountedRef = useRef(true);
  const detailsRequestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchTaskDetails = useCallback(() => {
    const currentRequestId = ++detailsRequestIdRef.current;
    setDetailsLoading(true);

    (async () => {
      try {
        const { data } = await api.get(`/tasks/${taskId}`);

        if (!isMountedRef.current || currentRequestId !== detailsRequestIdRef.current) return;
        setTask(data);

        // Somente o responsável atual consome a notificação. A chamada é
        // idempotente e o socket atualiza o selo do menu após a confirmação.
        if (Number(data.assignedUserId) === Number(user.id)) {
          api.post(`/tasks/${taskId}/assignment-seen`).catch(markError => {
            // Uma transferência simultânea pode tornar a atribuição
            // inacessível entre o GET e o POST; nesse caso não há erro útil
            // para exibir ao usuário.
            if (markError?.response?.status !== 404) toastError(markError);
          });
        }
      } catch (err) {
        if (!isMountedRef.current || currentRequestId !== detailsRequestIdRef.current) return;

        toastError(err);

        // 404: a tarefa sumiu/ficou inacessível — fecha e nunca tenta de
        // novo. Outros erros na carga inicial só ficam registrados via
        // toast; o modal permanece aberto mostrando o loading vazio.
        if (err?.response?.status === 404) {
          if (typeof onTaskUnavailable === "function") onTaskUnavailable(taskId);
          onClose();
          return;
        }
      } finally {
        if (isMountedRef.current && currentRequestId === detailsRequestIdRef.current) {
          setDetailsLoading(false);
        }
      }
    })();
    // onClose/onTaskUnavailable são callbacks do pai — deliberadamente fora
    // do array de dependências para não recriar esta função a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, user.id]);

  useEffect(() => {
    // Troca de tarefa: invalida qualquer requisição de detalhes anterior,
    // zera estado local e só então consulta a nova.
    detailsRequestIdRef.current += 1;
    setTask(null);
    setActiveTab("details");
    setIsEditing(false);
    fetchTaskDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const handleActionError = err => {
    toastError(err);
    const status = err?.response?.status;

    if (status === 404) {
      if (typeof onTaskUnavailable === "function") onTaskUnavailable(taskId);
      onClose();
      return;
    }

    if (status === 409) {
      // Status pode ter mudado em outra sessão — recarrega os detalhes
      // reais em vez de manter a tela desatualizada. Não tenta de novo a
      // mesma ação.
      fetchTaskDetails();
    }
    // Outros erros: modal/diálogo permanece aberto, nada mais a fazer aqui.
  };

  const applyTaskUpdate = updatedTask => {
    setTask(updatedTask);
    setIsEditing(false);
    if (typeof onTaskUpdated === "function") onTaskUpdated(updatedTask);
    // Reseta e recarrega o histórico (página 1) — nunca mantém a timeline
    // antiga depois de uma ação.
    setHistoryRefreshToken(t => t + 1);
  };

  // Função central de execução de ação: chama o endpoint, aplica o
  // resultado em caso de sucesso, roteia o erro em caso de falha. Retorna
  // um booleano para o diálogo decidir se fecha (sucesso) ou permanece
  // aberto (falha) — nunca mistura os dois.
  const runAction = async requestFn => {
    try {
      const data = await requestFn();
      applyTaskUpdate(data);
      return true;
    } catch (err) {
      handleActionError(err);
      return false;
    }
  };

  const handleEditSubmit = payload =>
    runAction(async () => (await api.put(`/tasks/${taskId}`, payload)).data);

  const handleStart = () => runAction(async () => (await api.post(`/tasks/${taskId}/start`)).data);

  const handleComplete = payload =>
    runAction(async () => (await api.post(`/tasks/${taskId}/complete`, payload)).data);

  const handleTransfer = payload =>
    runAction(async () => (await api.post(`/tasks/${taskId}/transfer`, payload)).data);

  const handleCancel = payload =>
    runAction(async () => (await api.post(`/tasks/${taskId}/cancel`, payload)).data);

  const handleTabChange = (_, newTab) => {
    setActiveTab(newTab);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="task-details-dialog-title"
    >
      <DialogTitle id="task-details-dialog-title" disableTypography>
        <Box className={classes.titleRow}>
          <Typography variant="h6" className={classes.titleText}>
            {task ? task.title : i18n.t("tasks.details.title")}
          </Typography>
          <IconButton aria-label={i18n.t("tasks.details.close")} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {task && (
          <Box className={classes.chipsRow}>
            <TaskStatusChip status={task.status} />
            <TaskPriorityChip priority={task.priority} />
          </Box>
        )}
      </DialogTitle>

      {detailsLoading || !task ? (
        <DialogContent>
          <Box className={classes.centered}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "fullWidth" : "standard"}
            indicatorColor="primary"
            textColor="primary"
            TabIndicatorProps={darkTabIndicatorProps}
          >
            <Tab style={darkTabStyle} label={i18n.t("tasks.details.tabs.details")} value="details" />
            <Tab style={darkTabStyle} label={i18n.t("tasks.details.tabs.history")} value="history" />
            <Tab style={darkTabStyle} label={i18n.t("tasks.details.tabs.comments")} value="comments" />
          </Tabs>

          <DialogContent dividers>
            {activeTab === "details" && (
              <TaskDetailsTab
                task={task}
                isEditing={isEditing}
                onEditSubmit={handleEditSubmit}
                onEditCancel={() => setIsEditing(false)}
              />
            )}
            {activeTab === "history" && (
              <TaskHistoryTab taskId={taskId} refreshToken={historyRefreshToken} />
            )}
            {activeTab === "comments" && (
              <TaskCommentsTab taskId={taskId} readOnly={readOnly} />
            )}
          </DialogContent>

          <DialogActions className={classes.actions}>
            {!readOnly && (
              <TaskActionsBar
                task={task}
                user={user}
                isMobile={isMobile}
                isEditing={isEditing}
                onEditToggle={() => setIsEditing(true)}
                onStart={handleStart}
                onComplete={handleComplete}
                onTransfer={handleTransfer}
                onCancel={handleCancel}
                onNotifyContactSuccess={applyTaskUpdate}
              />
            )}
            <Button onClick={onClose} fullWidth={isMobile}>
              {i18n.t("tasks.details.close")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default TaskDetailsModal;
